/**
 * Minimal axios trace instrumentation — FE Phase 2.
 *
 * Wraps an axios instance so every request:
 *   - opens a CLIENT span (HTTP <METHOD> <path>),
 *   - injects W3C `traceparent` into the outgoing headers so the BACKEND
 *     continues the SAME distributed trace,
 *   - stashes { trace_id, span_id } on the request config so the app's error
 *     logging (api.ts) can correlate a failed request to its span,
 *   - ends the span on response / error with status + error details.
 *
 * The tracer is resolved per-request against the global provider, so this is
 * safe to attach before initTracing() has run (early requests simply use a
 * no-op tracer and inject nothing). No-op overall when tracing is disabled.
 */
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { SpanStatusCode, SpanKind, type Span } from '@opentelemetry/api';
// Import from ./trace-api (api-only), NOT ./tracing, so the heavy OTel Web SDK
// stays out of the main bundle — it is loaded lazily by initTracing().
import { getTracer, injectTraceContext } from './trace-api';

const SPAN_KEY = Symbol('otel.span');

/** Trace ids stashed on a request config for downstream log correlation. */
export interface RequestTrace {
  trace_id: string;
  span_id: string;
}
type TracedConfig = InternalAxiosRequestConfig & {
  [SPAN_KEY]?: Span;
  __otelTrace?: RequestTrace;
};

/** Read trace ids stashed on a (possibly failed) request config. */
export function getRequestTrace(config: unknown): RequestTrace | undefined {
  return (config as TracedConfig | null | undefined)?.__otelTrace;
}

export function instrumentAxios(api: AxiosInstance): void {
  api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const cfg = config as TracedConfig;
    const method = (cfg.method || 'get').toUpperCase();
    const path = (cfg.url || '').split('?')[0];

    const span = getTracer().startSpan(`HTTP ${method} ${path}`, {
      kind: SpanKind.CLIENT,
      attributes: { 'http.request.method': method, 'url.path': path },
    });

    // Inject W3C trace context into the outgoing headers (no-op for a
    // non-recording span, so nothing leaks when tracing is disabled).
    const carrier: Record<string, string> = {};
    injectTraceContext(span, carrier);
    cfg.headers = cfg.headers || ({} as InternalAxiosRequestConfig['headers']);
    for (const [k, v] of Object.entries(carrier)) {
      (cfg.headers as Record<string, string>)[k] = v;
    }

    const sc = span.spanContext();
    if (sc.traceId && sc.traceId !== '0'.repeat(32)) {
      cfg.__otelTrace = { trace_id: sc.traceId, span_id: sc.spanId };
    }
    cfg[SPAN_KEY] = span;
    return cfg;
  });

  api.interceptors.response.use(
    (response) => {
      const span = (response.config as TracedConfig)[SPAN_KEY];
      if (span) {
        span.setAttribute('http.response.status_code', response.status);
        span.end();
      }
      return response;
    },
    (error: AxiosError) => {
      const cfg = (error.config || {}) as TracedConfig;
      const span = cfg[SPAN_KEY];
      const status = error.response?.status;
      if (span) {
        if (status) span.setAttribute('http.response.status_code', status);
        span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
        span.recordException(error);
        span.end();
      }
      return Promise.reject(error);
    }
  );
}
